import type { ApiClient } from '../api/client.js';
import { ApiError, isAbortError } from '../api/error.js';
import type { FloorPlan, Hotspot, Preset } from '../api/types.js';
import type { SceneMetadata, SceneMetadataKey } from '../state/types.js';

export type MetadataPartial =
  | { key: 'hotspots'; value: readonly Hotspot[] }
  | { key: 'presets'; value: readonly Preset[] }
  | { key: 'floorplan'; value: FloorPlan | null };

export interface MetadataWarning {
  readonly key: SceneMetadataKey;
  readonly reason: string; // ApiError.code, 'network', 'aborted-but-recovered', 'unknown'
}

export interface MetadataResult {
  readonly metadata: SceneMetadata;
  readonly warnings: readonly MetadataWarning[];
}

export interface FetchMetadataOptions {
  signal?: AbortSignal;
  onPartial?: (p: MetadataPartial) => void;
}

const DEFAULTS: SceneMetadata = Object.freeze({
  hotspots: Object.freeze([]) as readonly Hotspot[],
  presets: Object.freeze([]) as readonly Preset[],
  floorplan: null,
});

function classifyError(err: unknown): string {
  if (err instanceof ApiError) return err.code;
  if (err instanceof Error && err.name === 'NetworkError') return 'network';
  return 'unknown';
}

// Tolerant parallel fetch of auxiliary metadata. Per Codex iter 1 review:
// hotspots/presets/floorplan are NOT mandatory for core viewer integration
// (4.C). A failure on any of them must NOT abort the model load — it
// returns the default value and a warning so 4.D can surface it as a toast.
//
// Only AbortError is re-thrown so the orchestrator can cancel cleanly.
export async function fetchMetadataTolerant(
  client: ApiClient,
  modelId: string,
  opts: FetchMetadataOptions = {},
): Promise<MetadataResult> {
  const warnings: MetadataWarning[] = [];
  const callOpts = opts.signal !== undefined ? { signal: opts.signal } : undefined;

  const wrap = async <K extends SceneMetadataKey>(
    key: K,
    promise: Promise<SceneMetadata[K]>,
    fallback: SceneMetadata[K],
  ): Promise<SceneMetadata[K]> => {
    try {
      const value = await promise;
      opts.onPartial?.({ key, value } as MetadataPartial);
      return value;
    } catch (err) {
      if (isAbortError(err)) throw err;
      warnings.push({ key, reason: classifyError(err) });
      return fallback;
    }
  };

  const [hotspots, presets, floorplan] = await Promise.all([
    wrap('hotspots', client.fetchHotspots(modelId, callOpts), DEFAULTS.hotspots),
    wrap('presets', client.fetchPresets(modelId, callOpts), DEFAULTS.presets),
    wrap('floorplan', client.fetchFloorPlan(modelId, callOpts), DEFAULTS.floorplan),
  ]);

  return {
    metadata: { hotspots, presets, floorplan },
    warnings,
  };
}
